<template>
  <div class="i-reserve-booking">
    <div class="heading">
      Reserve Your Spot
      <div class="subheading">
        <template v-if="!props.booking"
          >Book your Discovery Session to unlock the next step <br v-if="isXsOrGreater" />in your coaching
          journey.</template
        >
        <template v-else
          >⏳ Your booking is reserved for <span class="gradient">{{ formattedTime }}</span> — checkout
          <template v-if="userStore.isCoachingHasEngaged || !userStore.isExecutiveCoachingPotential">
            or subscribe now </template
          >to keep your spot</template
        >
      </div>
    </div>

    <div
      v-if="!props.booking"
      class="action"
    >
      <iBaseButton
        v-bind="buttonStyle"
        @clicked="props.showProcessing ? null : openCalcomModal()"
      />
      <div>Coaching slots are in high demand 🔥, <br />lock in your time now.</div>
    </div>

    <div
      v-else
      class="booked"
    >
      <template v-if="!props.showProcessing">
        <div class="left">
          <div class="gradient first">Your Booking</div>
          <div class="second">{{ `${props.booking.length}-min ${props.booking.title}` }}</div>
          <div class="date">
            <img :src="iconCalendar" />
            <span>{{ new Date(props.booking.startTime).toLocaleString("en-US", localeDateStringOptions) }}</span>
            <img
              :src="iconEdit"
              @click="openCalcomModal()"
            />
          </div>
        </div>
        <div class="right">
          <img :src="assignedCoach.profilePhotoUrl || ''" />
          <div class="coach">
            <div class="name">{{ assignedCoach.fullName }}</div>
            <div>{{ assignedCoach.email }}</div>
          </div>
        </div>
      </template>
      <img
        v-else
        class="spinner"
        :src="iconSpinner"
      />
    </div>

    <iBaseModal
      class="embed"
      :visible="data.showCalcomEmbedModal"
      :fully-customisable="true"
      max-width="100%"
      @click-on-close="closeCalcomModal"
    >
      <div class="confirmation-modal">
        <iCalcomScheduler
          :key="data.calcomEmbedLink"
          :on-booking-successful="bookingSuccessful"
          :embed-link="data.calcomEmbedLink"
          version="inline"
        />
      </div>
    </iBaseModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue"
import { useCoachingStore } from "@/stores/coaching"
import { useUserStore } from "@/stores/user"
import { bluePurpleGradient, white } from "@/styles/colors"
import { breakpointXs } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"
import UrlService from "@/services/urlService"

import iBaseButton from "@/components/base/BaseButton.vue"
import iBaseModal from "@/components/base/BaseModal.vue"
import iCalcomScheduler from "@/components/global/CalcomScheduler.vue"

// ASSETS
import iconEdit from "@/assets/icons/icon-edit-pencil.svg"
import iconCalendar from "@/assets/icons/icon-blue-calendar.svg"
import iconSpinner from "@/assets/images/spinner.gif"

// TYPES
import CalcomBooking from "@/shared/types/CalcomBooking"

// EMITS
const emit = defineEmits(["fetch-booking", "update-countdown", "trigger-checkout", "revert-triggers"])

// STORES AND COMPOSABLES
const coachingStore = useCoachingStore()
const userStore = useUserStore()
const isXsOrGreater = useIsScreenSize(breakpointXs)

// PROPS
const props = defineProps({
  booking: {
    type: Object as () => CalcomBooking,
    default: null
  },
  checkoutTrigerred: {
    type: Boolean,
    default: false
  },
  showProcessing: {
    type: Boolean,
    default: false
  }
})

// CONSTANTS
const localeDateStringOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
  timeZoneName: "short",
  hour: "2-digit",
  minute: "2-digit"
} as const

// DATA
const data = reactive({
  showCalcomEmbedModal: false,
  calcomEmbedLink: "",
  interval: undefined as undefined | ReturnType<typeof setInterval>
})
const timeRemaining = ref(0)

// COMPUTED
const assignedCoach = computed(() => coachingStore.getAssignedCoach)
const buttonStyle = computed(() => {
  return {
    backgroundImage: bluePurpleGradient,
    borderRadius: "8px",
    fontWeight: "500",
    textColor: white,
    width: isXsOrGreater.value ? "300px" : "100%",
    padding: "12px 24px",
    size: "medium",
    text: props.showProcessing ? "Processing..." : "Book your Discovery Session",
    preIcon: iconCalendar
  }
})
const formattedTime = computed(() => {
  const minutes = Math.floor((timeRemaining.value / 60) % 60)
  const seconds = Math.floor(timeRemaining.value % 60)

  return `${minutes < 1 ? "" : minutes == 1 ? "1 min" : minutes + " mins"} ${seconds < 10 ? "0" + seconds : seconds} secs`
})

// FUNCTIONS
const openCalcomModal = () => {
  const calcomUsername = assignedCoach.value.calcomUsername
  if (!calcomUsername) return

  if (!userStore.isCoachingHasEngaged && userStore.isExecutiveCoachingPotential) {
    data.calcomEmbedLink = UrlService.generateCalComEmbedLink(`${calcomUsername}/discovery-60`)
  } else {
    data.calcomEmbedLink = UrlService.generateCalComEmbedLink(`${calcomUsername}/discovery-30`)
  }
  data.showCalcomEmbedModal = true
}

const closeCalcomModal = () => {
  data.showCalcomEmbedModal = false
  emit("revert-triggers")
}

const getTimeRemaining = () => {
  if (!props.booking) return 0
  const now = new Date()
  const diffInMs = now.getTime() - new Date(props.booking.createdAt).getTime()
  const diffInSecs = 900 - Math.floor(diffInMs / 1000)
  return diffInSecs
}

const bookingSuccessful = () => {
  localStorage.removeItem("checklist-reminder-checked")
  closeCalcomModal()
  emit("fetch-booking", true)

  if (props.checkoutTrigerred) emit("trigger-checkout")
}

const startTimer = () => {
  clearInterval(data.interval)
  setStartTime()

  data.interval = setInterval(() => {
    if (timeRemaining.value > 0) timeRemaining.value--

    if (timeRemaining.value >= 0) {
      localStorage.setItem("booking-timer", timeRemaining.value.toString())
    }

    if (timeRemaining.value <= 0) {
      emit("fetch-booking", false)
      clearInterval(data.interval)
    }
  }, 1000)
}

const setStartTime = () => {
  timeRemaining.value = getTimeRemaining() > 0 ? getTimeRemaining() : 0
  localStorage.setItem("booking-timer", timeRemaining.value.toString())
  timeRemaining.value = parseInt(localStorage.getItem("booking-timer") || timeRemaining.value.toString())
}

// LIFECYCLE HOOKS
onMounted(async () => {
  if (props.booking) startTimer()
})

onBeforeUnmount(() => {
  clearInterval(data.interval)
})

// WATCHERS
watch(
  () => props.booking?.createdAt,
  () => {
    startTimer()
  }
)
watch(
  () => timeRemaining.value,
  (newValue) => {
    if (newValue > 0) emit("update-countdown", formattedTime.value)
    else emit("update-countdown", "")
  }
)
watch(
  () => props.checkoutTrigerred,
  (newValue) => {
    if (newValue && !props.booking) openCalcomModal()
  }
)
</script>

<style lang="scss" scoped>
.i-reserve-booking {
  @include flex(column, start, center, $gap: 24px);

  padding: 35px 20px 15px;

  .gradient {
    background-image: $blue-purple-gradient;
    color: transparent;
    background-clip: text;
  }

  .heading {
    @include flex(column, start, center, $gap: 6px);
    @include text-xl;

    font-weight: 700;
    text-align: center;

    @media screen and (min-width: $breakpoint-md) {
      @include display-xs;
    }

    .subheading {
      @include text-sm;

      font-weight: 400;

      @media screen and (min-width: $breakpoint-md) {
        @include text-md;
      }
    }
  }

  .action {
    @include flex(column, start, stretch, $gap: 12px);
    @include text-sm;

    text-align: center;
    color: $comet700;
  }

  .embed {
    padding: 30px 0;

    @media screen and (min-width: $breakpoint-lg) {
      padding: unset;
    }

    .confirmation-modal {
      @include flex($direction: column, $justify: start, $align: center);

      margin: 10px;

      @media screen and (min-width: $breakpoint-md) {
        margin: 25px;
      }
    }
  }

  .booked {
    @include flex(row, start, stretch, wrap, $gap: 24px);
    @include border-radius-lg;

    width: 100%;
    border: 1px solid $zumthor100;
    background-color: $white;
    padding: 24px;
    min-height: 238px;

    @media screen and (min-width: $breakpoint-md) {
      min-height: 144px;
      max-width: 700px;
      justify-content: space-around;
    }

    @media screen and (min-width: $breakpoint-xl) {
      max-width: 818px;
    }

    .left {
      @include flex(column, start, start, $gap: 12px);
      @include text-sm;

      .first {
        font-weight: 500;
      }

      .second {
        font-weight: 600;
      }

      .date {
        @include flex(row, start, center, $gap: 8px);

        color: $comet700;
      }
    }

    .right {
      @include flex(row, center, center, $gap: 18px);

      img {
        @include border-radius-md;

        cursor: pointer;
        width: 70px;
        height: 70px;
      }

      .coach {
        @include flex(column, center, start);
        @include text-sm;

        .name {
          @include text-lg;

          font-weight: 600;
        }
      }
    }

    .spinner {
      width: 25px;
      height: 25px;
      margin: auto;
    }
  }
}
</style>
